import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { db, auth } from "../firebase";

function FraudRiskPage({
  users,
  auctions,
  transactions,
  adminLogs,
  dateFilteredAuctions,
  dateFilteredTransactions,
  onViewUser,
  onViewRelatedAuctions,
}) {
  const [riskSearch, setRiskSearch] = useState("");
  const [reviewedRiskKeys, setReviewedRiskKeys] = useState({});
  const [escalatedRiskKeys, setEscalatedRiskKeys] = useState({});
  const [clearedRiskKeys, setClearedRiskKeys] = useState({});
  const [adminNotesByRiskKey, setAdminNotesByRiskKey] = useState({});
  const [storedRiskReviews, setStoredRiskReviews] = useState({});

  useEffect(() => {
    const loadStoredRiskReviews = async () => {
      try {
        const snapshot = await getDocs(collection(db, "fraudRiskReviews"));

        const reviewsMap = {};

        snapshot.docs.forEach((item) => {
          reviewsMap[item.id] = {
            id: item.id,
            ...item.data(),
          };
        });

        setStoredRiskReviews(reviewsMap);
      } catch (error) {
        console.error("Load fraud risk reviews error:", error);
      }
    };

    loadStoredRiskReviews();
  }, []);

  const getTime = (value) => {
    if (!value) return 0;
    if (value?.seconds) return value.seconds * 1000;
    return Number(value || 0);
  };

  const formatDate = (value) => {
    const time = getTime(value);
    if (!time) return "—";
    return new Date(time).toLocaleString();
  };

  const getUserKey = (user) => user.uid || user.id || user.email || "";

  const getRiskLevel = (score) => {
    if (score >= 80) return "High";
    if (score >= 40) return "Medium";
    if (score > 0) return "Low";
    return "Clear";
  };

  const getRiskBadgeClass = (level) => {
    if (level === "High") return "badge failed";
    if (level === "Medium") return "badge pending";
    if (level === "Low") return "badge reviewed";
    return "badge succeeded";
  };

  const saveRiskReviewUpdate = async (riskKey, data) => {
    try {
      const safeRiskKey = String(riskKey || "").replace(/\//g, "_");

      await setDoc(
        doc(db, "fraudRiskReviews", safeRiskKey),
        {
          riskKey,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || "",
          ...data,
        },
        { merge: true }
      );

      setStoredRiskReviews((prev) => ({
        ...prev,
        [safeRiskKey]: {
          ...(prev[safeRiskKey] || {}),
          riskKey,
          ...data,
        },
      }));
    } catch (error) {
      console.error("Save fraud risk review error:", error);
      alert("Could not save fraud risk action. Please try again.");
    }
  };

  const addRiskNote = async (riskKey) => {
    const noteText = window.prompt("Write an admin risk note:");

    if (!noteText || !noteText.trim()) return;

    const safeRiskKey = String(riskKey || "").replace(/\//g, "_");

    const noteItem = {
      note: noteText.trim(),
      createdAt: new Date().toISOString(),
      createdBy: auth.currentUser?.email || "",
    };

    try {
      await updateDoc(doc(db, "fraudRiskReviews", safeRiskKey), {
        notes: arrayUnion(noteItem),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || "",
      });
    } catch (error) {
      await setDoc(
        doc(db, "fraudRiskReviews", safeRiskKey),
        {
          riskKey,
          notes: [noteItem],
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || "",
        },
        { merge: true }
      );
    }

    setStoredRiskReviews((prev) => ({
      ...prev,
      [safeRiskKey]: {
        ...(prev[safeRiskKey] || {}),
        notes: [...(prev[safeRiskKey]?.notes || []), noteItem],
      },
    }));
  };

  const fraudRiskRows = useMemo(() => {
    const riskMap = {};

    users.forEach((user) => {
      const userKey = getUserKey(user);

      if (!userKey) return;

      riskMap[userKey] = {
        riskKey: userKey,
        uid: user.uid || user.id || "",
        username: user.username || "Unknown User",
        email: user.email || "—",
        failedPayments: 0,
        reportsReceived: 0,
        removedAuctions: 0,
        flaggedAuctions: 0,
        adminCredits: 0,
        refundCredits: 0,
        suspiciousAuctions: 0,
        relatedAuctions: 0,
        installationCount: Array.isArray(user.installationIds)
          ? user.installationIds.length
          : 0,
        missingDeviceInfo: !user.deviceName && !user.deviceModel,
        missingSessionId: !user.currentSessionId,
        lastRiskActivity: 0,
        reasons: [],
      };
    });

    const ensureRiskRow = ({ key, username, email, uid }) => {
      if (!key) return null;

      if (!riskMap[key]) {
        riskMap[key] = {
          riskKey: key,
          uid: uid || "",
          username: username || "Unknown User",
          email: email || "—",
          failedPayments: 0,
          reportsReceived: 0,
          removedAuctions: 0,
          flaggedAuctions: 0,
          adminCredits: 0,
          refundCredits: 0,
          suspiciousAuctions: 0,
          relatedAuctions: 0,
          installationCount: 0,
          missingDeviceInfo: false,
          missingSessionId: false,
          lastRiskActivity: 0,
          reasons: [],
        };
      }

      return riskMap[key];
    };

    dateFilteredTransactions.forEach((transaction) => {
      const matchedUser = users.find(
        (user) =>
          user.uid === transaction.uid ||
          user.id === transaction.uid ||
          user.email === transaction.email
      );

      const userKey =
        matchedUser?.uid ||
        matchedUser?.id ||
        transaction.uid ||
        transaction.email ||
        "";

      const row = ensureRiskRow({
        key: userKey,
        username: matchedUser?.username || transaction.email || "Unknown User",
        email: matchedUser?.email || transaction.email || "—",
        uid: matchedUser?.uid || transaction.uid || "",
      });

      if (!row) return;

      const transactionType = String(transaction.type || "").toLowerCase();
      const transactionTime = getTime(transaction.createdAt);

      if (transaction.status === "failed") {
        row.failedPayments += 1;
        row.lastRiskActivity = Math.max(row.lastRiskActivity, transactionTime);
      }

      if (
        transactionType.includes("admin_credit") ||
        transactionType.includes("add_balance")
      ) {
        row.adminCredits += 1;
        row.lastRiskActivity = Math.max(row.lastRiskActivity, transactionTime);
      }

      if (
        transactionType.includes("refund") ||
        transactionType.includes("admin_refund_credit")
      ) {
        row.refundCredits += 1;
        row.lastRiskActivity = Math.max(row.lastRiskActivity, transactionTime);
      }
    });

    dateFilteredAuctions.forEach((auction) => {
      const sellerEmail = auction.createdBy || "";
      const sellerUsername = auction.createdByUsername || "";

      const matchedUser = users.find(
        (user) =>
          user.email === sellerEmail ||
          user.username === sellerUsername
      );

      const sellerKey =
        matchedUser?.uid ||
        matchedUser?.id ||
        matchedUser?.email ||
        sellerEmail ||
        sellerUsername ||
        "";

      const row = ensureRiskRow({
        key: sellerKey,
        username: sellerUsername || matchedUser?.username || "Unknown Seller",
        email: sellerEmail || matchedUser?.email || "—",
        uid: matchedUser?.uid || matchedUser?.id || "",
      });

      if (!row) return;

      const auctionTime =
        getTime(auction.removedAt) ||
        getTime(auction.flaggedAt) ||
        getTime(auction.reportedAt) ||
        getTime(auction.createdAt);

      row.relatedAuctions += 1;

      if (auction.status === "removed") {
        row.removedAuctions += 1;
        row.lastRiskActivity = Math.max(row.lastRiskActivity, auctionTime);
      }

      if (auction.flagged === true) {
        row.flaggedAuctions += 1;
        row.lastRiskActivity = Math.max(row.lastRiskActivity, auctionTime);
      }

      if (
        auction.reported === true ||
        Number(auction.reportCount || 0) > 0
      ) {
        row.reportsReceived += Number(auction.reportCount || 1);
        row.lastRiskActivity = Math.max(row.lastRiskActivity, auctionTime);
      }

      if (
        auction.flagged === true ||
        auction.status === "removed" ||
        Number(auction.reportCount || 0) >= 2
      ) {
        row.suspiciousAuctions += 1;
      }
    });

    adminLogs.forEach((log) => {
      const targetEmail = log.targetEmail || log.targetOwnerEmail || "";
      const targetUsername = log.targetUsername || log.targetOwner || "";
      const targetUid = log.targetUid || "";

      const matchedUser = users.find(
        (user) =>
          user.uid === targetUid ||
          user.id === targetUid ||
          user.email === targetEmail ||
          user.username === targetUsername
      );

      const userKey =
        matchedUser?.uid ||
        matchedUser?.id ||
        matchedUser?.email ||
        targetUid ||
        targetEmail ||
        targetUsername ||
        "";

      const row = ensureRiskRow({
        key: userKey,
        username: matchedUser?.username || targetUsername || "Unknown User",
        email: matchedUser?.email || targetEmail || "—",
        uid: matchedUser?.uid || matchedUser?.id || targetUid || "",
      });

      if (!row) return;

      const action = String(log.action || "").toLowerCase();
      const logTime = getTime(log.createdAt) || getTime(log.createdAtText);

      if (
        action.includes("refund") ||
        action.includes("promotional") ||
        action.includes("add_promotional_balance")
      ) {
        row.adminCredits += 1;
        row.lastRiskActivity = Math.max(row.lastRiskActivity, logTime);
      }

      if (
        action.includes("remove_auction") ||
        action.includes("flag_auction") ||
        action.includes("ban_user") ||
        action.includes("disable_user")
      ) {
        row.lastRiskActivity = Math.max(row.lastRiskActivity, logTime);
      }
    });

    return Object.values(riskMap)
      .map((row) => {
        const reasons = [];

        if (row.failedPayments >= 3) {
          reasons.push("Many failed payments");
        }

        if (row.reportsReceived >= 3) {
          reasons.push("Many reports received");
        }

        if (row.removedAuctions >= 2) {
          reasons.push("Multiple removed auctions");
        }

        if (row.flaggedAuctions >= 2) {
          reasons.push("Multiple flagged auctions");
        }

        if (row.adminCredits >= 3) {
          reasons.push("Many admin/free credits");
        }

        if (row.refundCredits >= 2) {
          reasons.push("Repeated refund credits");
        }

        if (row.suspiciousAuctions >= 2) {
          reasons.push("Multiple risky auctions");
        }

        if (row.installationCount >= 5) {
          reasons.push("Many device installations");
        }

        if (row.missingDeviceInfo) {
          reasons.push("Missing device info");
        }

        if (row.missingSessionId) {
          reasons.push("Missing active session ID");
        }

        const riskScore =
          row.failedPayments * 10 +
          row.reportsReceived * 8 +
          row.removedAuctions * 18 +
          row.flaggedAuctions * 12 +
          row.adminCredits * 5 +
          row.refundCredits * 8 +
          row.suspiciousAuctions * 15 +
          (row.installationCount >= 5 ? 15 : 0) +
          (row.missingDeviceInfo ? 8 : 0) +
          (row.missingSessionId ? 6 : 0);

        const riskLevel = getRiskLevel(riskScore);

        return {
          ...row,
          riskScore,
          riskLevel,
          reasons,
        };
      })
      .filter((row) => {
        const safeRiskKey = String(row.riskKey || "").replace(/\//g, "_");

        if (clearedRiskKeys[row.riskKey]) return false;
        if (storedRiskReviews[safeRiskKey]?.cleared === true) return false;

        return (
          row.riskScore > 0 ||
          row.failedPayments > 0 ||
          row.reportsReceived > 0 ||
          row.removedAuctions > 0 ||
          row.flaggedAuctions > 0 ||
          row.adminCredits > 0 ||
          row.refundCredits > 0 ||
          row.installationCount >= 5 ||
          row.missingDeviceInfo ||
          row.missingSessionId
        );
      })
      .sort((a, b) => Number(b.riskScore || 0) - Number(a.riskScore || 0));
  }, [
    users,
    dateFilteredAuctions,
    dateFilteredTransactions,
    adminLogs,
    clearedRiskKeys,
    storedRiskReviews,
  ]);

  const filteredRows = fraudRiskRows.filter((row) => {
    const searchText = riskSearch.toLowerCase().trim();

    if (!searchText) return true;

    return (
      String(row.username || "").toLowerCase().includes(searchText) ||
      String(row.email || "").toLowerCase().includes(searchText) ||
      String(row.uid || "").toLowerCase().includes(searchText) ||
      String(row.riskLevel || "").toLowerCase().includes(searchText)
    );
  });

  const highRiskCount = fraudRiskRows.filter(
    (row) => row.riskLevel === "High"
  ).length;

  const mediumRiskCount = fraudRiskRows.filter(
    (row) => row.riskLevel === "Medium"
  ).length;

  const lowRiskCount = fraudRiskRows.filter(
    (row) => row.riskLevel === "Low"
  ).length;

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Total Risk Rows</span>
          <strong>{fraudRiskRows.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>High Risk</span>
          <strong>{highRiskCount}</strong>
        </div>

        <div className="stat-card">
          <span>Medium Risk</span>
          <strong>{mediumRiskCount}</strong>
        </div>

        <div className="stat-card">
          <span>Low Risk</span>
          <strong>{lowRiskCount}</strong>
        </div>

        <div className="stat-card">
        <span>Reviewed Risk Cases</span>
          <strong>
            {
              Object.values(storedRiskReviews).filter(
                (item) => item.reviewed === true && item.cleared !== true
              ).length
            }
          </strong>
          <small style={{ display: "block", marginTop: 4 }}>
          Persisted in Firestore
          </small>
        </div>

        <div className="stat-card">
        <span>Escalated Active Risks</span>
          <strong>
            {
              Object.values(storedRiskReviews).filter(
                (item) => item.escalated === true && item.cleared !== true
              ).length
            }
          </strong>
          <small style={{ display: "block", marginTop: 4 }}>
          Persisted in Firestore
          </small>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Fraud Risk</h2>
            <p>
              Review users and sellers with risk signals. This page does not
              automatically punish users.
            </p>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search user, email, UID, or risk level..."
            value={riskSearch}
            onChange={(event) => setRiskSearch(event.target.value)}
          />
        </div>

        {filteredRows.length === 0 ? (
          <p className="empty-text">No fraud risk signals found.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Risk Level</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>UID</th>
                  <th>Risk Score</th>
                  <th>Failed Payments</th>
                  <th>Reports</th>
                  <th>Removed Auctions</th>
                  <th>Flagged Auctions</th>
                  <th>Admin Credits</th>
                  <th>Refund Credits</th>
                  <th>Suspicious Auctions</th>
                  <th>Related Auctions</th>
                  <th>Installations</th>
                  <th>Device Info</th>
                  <th>Session ID</th>
                  <th>Last Risk Activity</th>
                  <th>Reasons</th>
                  <th>Notes</th>
                  <th>Escalation Reason</th>
                  <th>Review Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.riskKey}>
                    <td>
                      <span className={getRiskBadgeClass(row.riskLevel)}>
                        {storedRiskReviews[
                          String(row.riskKey || "").replace(/\//g, "_")
                        ]?.escalated
                          ? `Escalated ${row.riskLevel}`
                          : row.riskLevel}
                      </span>
                    </td>

                    <td>{row.username || "—"}</td>
                    <td>{row.email || "—"}</td>
                    <td className="small-muted">{row.uid || "—"}</td>
                    <td>{Number(row.riskScore || 0)}</td>
                    <td>{Number(row.failedPayments || 0)}</td>
                    <td>{Number(row.reportsReceived || 0)}</td>
                    <td>{Number(row.removedAuctions || 0)}</td>
                    <td>{Number(row.flaggedAuctions || 0)}</td>
                    <td>{Number(row.adminCredits || 0)}</td>
                    <td>{Number(row.refundCredits || 0)}</td>
                    <td>{Number(row.suspiciousAuctions || 0)}</td>
                    <td>{Number(row.relatedAuctions || 0)}</td>
                    <td>{Number(row.installationCount || 0)}</td>
                    <td>{row.missingDeviceInfo ? "Missing" : "Available"}</td>
                    <td>{row.missingSessionId ? "Missing" : "Available"}</td>
                    <td>{formatDate(row.lastRiskActivity)}</td>

                    <td className="small-muted">
                      {row.reasons.length > 0
                        ? row.reasons.join(" | ")
                        : "Minor signal only"}
                    </td>

                    <td className="small-muted">
                      {storedRiskReviews[
                        String(row.riskKey || "").replace(/\//g, "_")
                      ]?.notes?.length
                        ? storedRiskReviews[
                            String(row.riskKey || "").replace(/\//g, "_")
                          ].notes
                            .map(
                              (item, index) =>
                                `${index + 1}. ${item.note} — ${
                                  item.createdBy || "Unknown admin"
                                } — ${formatDate(item.createdAt)}`
                            )
                            .join(" | ")
                        : "—"}
                    </td>

                    <td className="small-muted">
                      {storedRiskReviews[
                        String(row.riskKey || "").replace(/\//g, "_")
                      ]?.escalationReason || "—"}
                    </td>

                    <td>
                      <button
                        className="enable-action-btn"
                        onClick={() => onViewUser(row)}
                        style={{ marginRight: 6 }}
                      >
                        View User
                      </button>

                      <button
                        className="flag-action-btn"
                        onClick={() => onViewRelatedAuctions(row)}
                        style={{ marginRight: 6 }}
                      >
                        View Related Auctions
                      </button>

                      <button
                        className="enable-action-btn"
                        onClick={() =>
                          saveRiskReviewUpdate(row.riskKey, {
                            reviewed: true,
                            reviewedAt: serverTimestamp(),
                            reviewedBy: auth.currentUser?.email || "",
                          })
                        }
                        style={{ marginRight: 6 }}
                      >
                        {storedRiskReviews[
                          String(row.riskKey || "").replace(/\//g, "_")
                        ]?.reviewed
                          ? "Reviewed"
                          : "Mark Reviewed"}
                      </button>

                      <button
                        className="flag-action-btn"
                        onClick={() => addRiskNote(row.riskKey)}
                        style={{ marginRight: 6 }}
                      >
                        Add Admin Note
                      </button>

                      <button
                        className="danger-action-btn"
                        onClick={() => {
                          const escalationReason = window.prompt(
                            "Enter escalation reason:",
                            "Needs owner review"
                          );

                          if (!escalationReason || !escalationReason.trim()) {
                            return;
                          }

                          saveRiskReviewUpdate(row.riskKey, {
                            escalated: true,
                            escalationReason: escalationReason.trim(),
                            escalatedAt: serverTimestamp(),
                            escalatedBy: auth.currentUser?.email || "",
                          });
                        }}
                        style={{ marginRight: 6 }}
                      >
                        {storedRiskReviews[
                          String(row.riskKey || "").replace(/\//g, "_")
                        ]?.escalated
                          ? "Escalated"
                          : "Escalate Risk"}
                      </button>

                      <button
                        className="remove-action-btn"
                        onClick={() =>
                          saveRiskReviewUpdate(row.riskKey, {
                            cleared: true,
                            clearedAt: serverTimestamp(),
                            clearedBy: auth.currentUser?.email || "",
                          })
                        }
                      >
                        Clear Risk Flag
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default FraudRiskPage;